import {t} from '@lingui/core/macro';

import {SERVER_METRICS_CHART_COLORS} from 'shared/lib/colors';

import IServerOutput from '../model/Server/IServer';
import {IServerLastMetrics} from '../model/Server/IServerMetrics';

import {
    SERVER_METRICS_DEFAULT_RANGE_KEY,
    SERVER_METRICS_RANGE_PARAM,
    SERVER_METRICS_RANGE_SECONDS,
    ServerMetricsRangeKey,
} from './serverMetrics';
import {formatBytesToGb, isServerMetricsRangeKey, ServerMetricsChartRow} from './serverMetricsHelper';

const SECONDS_IN_DAY = 86400;

export interface IServerMetricsRange {
    from: number,
    to: number,
}

export interface IServerMetricsInitialParams {
    preset: ServerMetricsRangeKey | null,
    custom: IServerMetricsRange | null,
}

export interface IServerMetricsRangeOption {
    key: ServerMetricsRangeKey,
    label: string,
    requiredDays: number,
    disabled: boolean,
}

export interface IServerMetricsSeriesItem {
    key: string,
    label: string,
    color: string,
}

export interface IServerMetricsSeries {
    cpu: IServerMetricsSeriesItem[],
    la: IServerMetricsSeriesItem[],
    mem: IServerMetricsSeriesItem[],
    fs: IServerMetricsSeriesItem[],
}

export interface IServerMetricsSummary {
    cpuPercent: number,
    ramTotal: number | null | undefined,
    ramPercent: number,
    swapTotal: number | null | undefined,
    swapPercent: number,
    fs: {mount: string, percent: number}[],
    la: [number, number, number],
}

export const percentFormatter = (value: number) => `${value.toFixed(1)}%`;
export const loadFormatter = (value: number) => value.toFixed(2);

export const memoryFormatter = (value: number, row?: ServerMetricsChartRow, key?: string): string => {
    const used = formatBytesToGb(key === 'swap_used_pct' ? row?.swap_used_bytes : row?.mem_used_bytes, '');

    return used ? `${percentFormatter(value)} · ${used}` : percentFormatter(value);
};

export function hasServerMetricsValues(data: ServerMetricsChartRow[], seriesKeys: string[]): boolean {
    return data.some((row) => seriesKeys.some((key) => row[key] !== null));
}

export function parseServerMetricsRange(searchParams: URLSearchParams): IServerMetricsRange | null {
    const from = Number(searchParams.get('from'));
    const to = Number(searchParams.get('to'));

    if (Number.isFinite(from) && from > 0 && Number.isFinite(to) && to > from) {
        return {from, to};
    }

    return null;
}

export function getInitialServerMetricsParams(searchParams: URLSearchParams): IServerMetricsInitialParams {
    const presetParam = searchParams.get(SERVER_METRICS_RANGE_PARAM);

    return {
        preset: isServerMetricsRangeKey(presetParam) ? presetParam : null,
        custom: parseServerMetricsRange(searchParams),
    };
}

export function resolveServerMetricsRange(
    activePreset: ServerMetricsRangeKey | null,
    customRange: IServerMetricsRange | null,
    now: number,
): IServerMetricsRange {
    if (activePreset) {
        return {from: now - SERVER_METRICS_RANGE_SECONDS[activePreset], to: now};
    }

    return customRange ?? {
        from: now - SERVER_METRICS_RANGE_SECONDS[SERVER_METRICS_DEFAULT_RANGE_KEY],
        to: now,
    };
}

export function getServerMetricsGranularity(range: IServerMetricsRange): 'time' | 'date' {
    return range.to - range.from <= SERVER_METRICS_RANGE_SECONDS['24h'] ? 'time' : 'date';
}

export function buildServerMetricsSearchParams(
    prev: URLSearchParams,
    activePreset: ServerMetricsRangeKey | null,
    customFrom: number | null,
    customTo: number | null,
): URLSearchParams {
    const next = new URLSearchParams(prev);

    if (activePreset) {
        next.set(SERVER_METRICS_RANGE_PARAM, activePreset);
        next.delete('from');
        next.delete('to');
    } else {
        next.delete(SERVER_METRICS_RANGE_PARAM);
        next.set('from', String(customFrom));
        next.set('to', String(customTo));
    }

    return next;
}

export function getServerMetricsRangeOptions(retentionDays: number): IServerMetricsRangeOption[] {
    const options: {key: ServerMetricsRangeKey, label: string}[] = [
        {key: '1h', label: t`1 час`},
        {key: '24h', label: t`24 часа`},
        {key: '7d', label: t`7 дней`},
        {key: '30d', label: t`30 дней`},
    ];

    return options.map((option) => {
        const requiredDays = SERVER_METRICS_RANGE_SECONDS[option.key] / SECONDS_IN_DAY;

        return {...option, requiredDays, disabled: requiredDays > retentionDays};
    });
}

export function getServerMetricsSeries(): IServerMetricsSeries {
    const [first, second, third, fourth] = SERVER_METRICS_CHART_COLORS;

    return {
        cpu: [
            {key: 'cpu_user', label: t`User`, color: first},
            {key: 'cpu_system', label: t`System`, color: second},
            {key: 'cpu_iowait', label: t`IOWait`, color: third},
            {key: 'cpu_steal', label: t`Steal`, color: fourth},
        ],
        la: [
            {key: 'la1', label: t`1 мин.`, color: first},
            {key: 'la5', label: t`5 мин.`, color: second},
            {key: 'la15', label: t`15 мин.`, color: third},
        ],
        mem: [
            {key: 'mem_used_pct', label: t`RAM`, color: first},
            {key: 'swap_used_pct', label: t`Swap`, color: second},
        ],
        fs: [
            {key: 'used_pct', label: t`Занято`, color: first},
            {key: 'inodes_pct', label: t`Inodes`, color: second},
        ],
    };
}

export function getServerMetricsSummary(
    lastMetrics: IServerLastMetrics,
    server: Pick<IServerOutput, 'mem_total' | 'swap_total'>,
): IServerMetricsSummary {
    return {
        cpuPercent: 100 - (lastMetrics.cpu_idle ?? 100),
        ramTotal: lastMetrics.mem_total ?? server.mem_total,
        ramPercent: lastMetrics.mem_total
            ? ((lastMetrics.mem_total - (lastMetrics.mem_available ?? 0)) / lastMetrics.mem_total) * 100
            : 0,
        swapTotal: lastMetrics.swap_total ?? server.swap_total,
        swapPercent: lastMetrics.swap_total
            ? ((lastMetrics.swap_used ?? 0) / lastMetrics.swap_total) * 100
            : 0,
        fs: lastMetrics.fs.map((fs) => ({mount: fs.mount, percent: (fs.used_bytes / fs.total_bytes) * 100})),
        la: [lastMetrics.la1 ?? 0, lastMetrics.la5 ?? 0, lastMetrics.la15 ?? 0],
    };
}
